import { DataGrid } from "@msuite/ui/data-grid";
import { useDisclosure } from "@msuite/ui/hooks";
import { AlertIcon, Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { useQuery } from "@msuite/ui/query";
import { Tooltip } from "@msuite/ui/tooltip";
import { responseHandler } from "@msuite/ui/utils";
import {
	formatNumericPartnerAgreementValue,
	getPartnerAgreementTypeTranslationKey,
} from "@msuite/utils/partnerships";
import type { Enums } from "@msuite/utils/types";
import React from "react";
import { client } from "@/hono";
import { EditNumericAgreementModal } from "@/modals/partners/edit-numeric-agreement-modal";
import { NumericAgreementHistoryModal } from "@/modals/partners/numeric-agreement-history-modal";
import type { PartnerPanelBaseProps } from "../types";

/** Props Interface */
interface PartnerNumericAgreementsRowProps extends PartnerPanelBaseProps {
	type: Enums<"organizationPartnershipAgreementType">;
}

export function PartnerNumericAgreementsRow({
	type,
	orgId,
	partnershipId,
}: PartnerNumericAgreementsRowProps) {
	/** Hooks */
	const editAgreementModal = useDisclosure();
	const agreementsHistoryModal = useDisclosure();
	const { t } = useTranslation();
	const translationKey = getPartnerAgreementTypeTranslationKey(type);

	/** Query */
	const { data, refetch } = useQuery({
		queryKey: ["partner", "agreements", partnershipId, type],
		queryFn: async () => {
			const response = await client.organizations[":orgId"].partnerships[
				":partnershipId"
			].agreements[":agreementType"].$get({
				param: {
					orgId,
					partnershipId,
					agreementType: type,
				},
				query: {
					agreementId: undefined,
				},
			});
			const data = await responseHandler(response);
			return data;
		},
	});

	const hasValue = !!data?.active?.value;
	const value = formatNumericPartnerAgreementValue(data?.active, t);

	/** Render */
	return (
		<React.Fragment>
			<DataGrid.Label>{t(translationKey)}</DataGrid.Label>
			<DataGrid.Value
				onEdit={editAgreementModal.onOpen}
				justify="end"
				append={
					hasValue ? (
						<Tooltip label={t("general.history")}>
							<Icon
								name="History"
								className="opacity-50 hover:opacity-100"
								onClick={agreementsHistoryModal.onOpen}
							/>
						</Tooltip>
					) : (
						<AlertIcon />
					)
				}
			>
				<div className="items-baseline w-max h-stack">
					{data?.active?.note && (
						<span className="translate-y-0.5">
							<Tooltip label={data.active.note}>
								<Icon
									name="Info"
									className="opacity-50"
								/>
							</Tooltip>
						</span>
					)}
					{value}
				</div>
			</DataGrid.Value>
			{editAgreementModal.isOpen && (
				<EditNumericAgreementModal
					isOpen={editAgreementModal.isOpen}
					onClose={editAgreementModal.onClose}
					partnershipId={partnershipId}
					orgId={orgId}
					type={type}
					onCompleteEdit={refetch}
					history={data?.history ?? []}
					agreementId={undefined}
				/>
			)}
			{agreementsHistoryModal.isOpen && (
				<NumericAgreementHistoryModal
					isOpen={agreementsHistoryModal.isOpen}
					onClose={agreementsHistoryModal.onClose}
					agreements={data?.history ?? []}
					onCompleteEdit={refetch}
					orgId={orgId}
					partnershipId={partnershipId}
				/>
			)}
		</React.Fragment>
	);
}
